/**
 * <predictive-search> — header search with vehicle-aware results.
 *
 * Two sources, deliberately different:
 *
 *  - "All products" uses Shopify's predictive search (/search/suggest.json),
 *    which is backed by Search & Discovery, so relevance and synonyms are
 *    whatever the merchant has configured.
 *
 *  - "My vehicle" matches against that vehicle's whole collection, loaded
 *    once through CollectionFeed (every page, cached per tab, prefetched as
 *    soon as a machine is chosen) and matched word-prefix by word-prefix in
 *    the browser. Shopify's predictive search cannot be scoped to a
 *    collection; this can, and it answers on every keystroke with no request.
 *
 * With a machine selected, its matches are shown FIRST, under a "Fits your…" band,
 * followed by everything else — so a scoped search never hides a product that
 * exists elsewhere, and never presents an unchecked one as a fit. The local
 * results paint at once; Shopify's arrive a moment later and are merged in.
 */
const SEARCH_DEBOUNCE_MS = 150;

/** Room for the arrow beyond the text and the padding: none, since the
 * select draws its own chevron inside its right padding (request 148). */
const SCOPE_ARROW_PX = 0;

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;

    this.input = this.querySelector('[data-search-input]');
    this.scope = this.querySelector('[data-search-scope]');
    this.results = this.querySelector('[data-search-results]');
    this.form = this.querySelector('form');
    this.limit = Number(this.dataset.limit) || 6;

    this.activeIndex = -1;
    this.items = [];

    this.input.addEventListener('input', () => this.schedule());
    this.input.addEventListener('focus', () => { if (this.input.value.trim()) this.schedule(); });
    this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    this.scope.addEventListener('change', () => {
      this.syncHiddenScope();
      this.fitScope();
      // A change event on a select only fires from real interaction — setting
      // .value in code doesn't — so this is a reliable signal of intent.
      // Don't infer it from document.activeElement; focus isn't guaranteed.
      this.userPickedScope = true;
      this.schedule(true);
    });

    // Close on outside click, but not when the click lands inside the dropdown.
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.close();
    });

    document.addEventListener('garage:changed', () => {
      this.syncScopeOptions();
      this.prefetch();
    });
    this.syncScopeOptions();
    this.prefetch();
    // The web font may land after the first fit: measure again once it has.
    document.fonts?.ready?.then?.(() => this.fitScope());
  }

  /**
   * The scope is as wide as the machine it names and no wider: a <select>
   * sizes itself to its widest option, which left a short name floating in
   * white space before the arrow (Lucas, 2026-10-10). The selected option is
   * measured in the select's own font, and the padding and the arrow added;
   * the stylesheet's max-width still caps a long nickname. On a phone the
   * scope is a full-width box whose flex basis ignores this width.
   */
  fitScope() {
    const option = this.scope.options[this.scope.selectedIndex];
    if (!option) return;
    const style = getComputedStyle(this.scope);
    const mirror = document.createElement('span');
    mirror.style.cssText = 'position:absolute;left:-9999px;top:0;visibility:hidden;white-space:nowrap;';
    for (const prop of ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing']) {
      mirror.style[prop] = style[prop];
    }
    mirror.textContent = option.textContent;
    document.body.append(mirror);
    const text = mirror.getBoundingClientRect().width;
    mirror.remove();
    if (!text) return; // not laid out — hidden, or no layout engine
    const sides = style.boxSizing === 'border-box'
      ? ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth'].reduce((n, prop) => n + (parseFloat(style[prop]) || 0), 0)
      : 0;
    this.scope.style.width = `${Math.ceil(text + sides + SCOPE_ARROW_PX)}px`;
  }

  /**
   * Rebuild the scope dropdown from the whole garage — every saved machine gets
   * an option, so the customer can search any of them without switching which
   * one the rest of the site is using. Scope values are vehicle keys; "all" is
   * the only reserved value.
   */
  syncScopeOptions() {
    const vehicles = window.Garage?.vehicles ?? [];
    const active = window.Garage?.active();
    const previous = this.scope.value;

    const frag = document.createDocumentFragment();
    for (const vehicle of vehicles) {
      const option = document.createElement('option');
      option.value = vehicle.key;
      // textContent, not innerHTML — nicknames are customer input.
      option.textContent = vehicle.nickname || vehicle.label;
      frag.appendChild(option);
    }

    const all = document.createElement('option');
    all.value = 'all';
    all.textContent = 'All products';
    frag.appendChild(all);

    this.scope.replaceChildren(frag);

    // Keep an explicit choice if that machine is still in the garage; otherwise
    // follow the active one, since someone who picked a machine almost always
    // wants parts that fit it.
    const stillValid = this.userPickedScope
      && [...this.scope.options].some((o) => o.value === previous);

    this.scope.value = stillValid ? previous : (active?.key ?? 'all');
    this.syncHiddenScope();
    this.fitScope();
  }

  /**
   * Enter and "view all" must keep the scope: the form carries it as hidden
   * fields (scope=machine&vehicle=<key>) that the search page reads, so a
   * search typed against a machine lands on that machine's results.
   */
  syncHiddenScope() {
    const form = this.querySelector('form');
    if (!form) return;
    const machine = this.scope.value && this.scope.value !== 'all' ? this.scope.value : '';
    for (const [name, value] of [['scope', machine ? 'machine' : ''], ['vehicle', machine]]) {
      let input = form.querySelector(`input[name="${name}"]`);
      if (!value) { input?.remove(); continue; }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      input.value = value;
    }
  }

  /** The machine's feed, loaded before it is needed. */
  prefetch() {
    const key = this.scope.value && this.scope.value !== 'all' ? this.scope.value : (window.Garage?.active()?.key ?? null);
    if (key && window.CollectionFeed) window.CollectionFeed.load(key).catch(() => {});
  }

  /**
   * Local matches paint on every keystroke, straight away; Shopify's request
   * waits for a pause in typing and is merged in when it arrives.
   */
  schedule(immediate = false) {
    clearTimeout(this.timer);
    this.runLocal();
    if (immediate) this.run();
    else this.timer = setTimeout(() => this.run(), SEARCH_DEBOUNCE_MS);
  }

  currentVehicle() {
    const scopeKey = this.scope.value;
    if (scopeKey === 'all') return null;
    return (window.Garage?.vehicles ?? []).find((v) => v.key === scopeKey) ?? null;
  }

  /** The machine's matches alone, from what is already in hand — no request. */
  runLocal() {
    const query = this.input.value.trim();
    if (query.length < 2) return this.close();
    const vehicle = this.currentVehicle();
    if (!vehicle || !window.CollectionFeed) return;
    const products = window.CollectionFeed.peek(vehicle.key);
    if (!products) return;
    const hits = this.matchLocal(products, query);
    this.lastLocal = { query, hits };
    this.render(query, [
      { title: `Fits your ${vehicle.nickname || vehicle.label}`, products: hits, fits: true },
      { title: 'More products', products: [], pending: true },
    ]);
  }

  async run() {
    const query = this.input.value.trim();
    if (query.length < 2) return this.close();

    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;

    // The scope value is a vehicle key, so searching a machine other than the
    // active one works without touching the garage.
    const scopeKey = this.scope.value;
    const vehicle = scopeKey === 'all'
      ? null
      : (window.Garage?.vehicles ?? []).find((v) => v.key === scopeKey);

    try {
      const [vehicleHits, globalHits, localMachines] = await Promise.all([
        vehicle ? this.searchCollection(vehicle.key, query, signal) : [],
        this.searchAll(query, signal),
        this.searchMachines(query),
      ]);
      // The taxonomy's matches (any part of a name: "400r" finds XR400R)
      // first, then Shopify's whole-word collection suggestions for a make
      // the shipped taxonomy does not carry; one row per machine.
      const seenMachines = new Set();
      const machines = [
        ...localMachines.map((v) => ({ handle: v.key, title: v.label })),
        ...(globalHits.machines ?? []),
      ].filter((m) => m.handle && !seenMachines.has(m.handle) && seenMachines.add(m.handle)).slice(0, 6);

      const seen = new Set(vehicleHits.map((p) => p.handle));
      const others = globalHits.filter((p) => !seen.has(p.handle));
      const rest = others.filter((p) => p.available !== false).concat(others.filter((p) => p.available === false));

      this.render(query, [
        machines.length && {
          title: 'Machines',
          note: 'Every part on a machine\'s page fits it',
          products: machines,
          machines: true,
        },
        vehicle && {
          title: `Fits your ${vehicle.nickname || vehicle.label}`,
          products: vehicleHits,
          fits: true,
        },
        {
          title: vehicle ? 'More products' : 'Products',
          note: vehicle ? 'Not checked against your machine' : '',
          products: rest,
        },
      ].filter(Boolean));
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[predictive-search]', err);
      this.renderMessage('Search is unavailable right now.');
    }
  }

  /**
   * Machines from the fitment taxonomy (fitment-data.js), which every page
   * can load: tokens match anywhere in "year make model", so "1999 honda
   * 400r" finds the 1999 Honda XR400R (#84). Asked only for a query of two
   * or more words or one with a year in it — a lone "polaris" is a product
   * search. Empty when the taxonomy cannot be had; Shopify's suggestions
   * stand in then.
   */
  async searchMachines(query) {
    const toks = String(query).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    if (!window.FitmentData || !(toks.length >= 2 || toks.some((t) => /^(19|20)\d\d$/.test(t)))) return [];
    try {
      if (!window.FitmentData.ready) await window.FitmentData.load();
      return window.FitmentData.search(query, 6);
    } catch {
      return [];
    }
  }

  /**
   * Shopify predictive search — merchant-configured relevance. Products, and
   * collections: the store's manual collections are its machines, so "2022
   * polaris sportsman" finds the machines themselves (Lucas, 2026-10-12).
   * A machine is a collection whose handle starts with its year.
   */
  async searchAll(query, signal) {
    const url = `${window.Shopify?.routes?.root ?? '/'}search/suggest.json`
      + `?q=${encodeURIComponent(query)}`
      + `&resources[type]=product,collection&resources[limit]=${this.limit}`;

    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error(`suggest ${res.status}`);
    const body = await res.json();
    const results = body?.resources?.results ?? {};
    const machines = (results.collections ?? []).filter((c) => /^\d{4}-/.test(c.handle || ''));
    const products = results.products ?? [];
    this.lastMachines = machines;
    return Object.assign(products, { machines });
  }

  /**
   * Local match over the vehicle's whole collection. Every token the customer
   * typed must start a word — in the title, the part number, the brand or
   * the product type — so "800 belt" finds a drive belt for an 800 in any
   * order, and "oil f" finds oil filters without finding "Foil".
   */
  async searchCollection(key, query, signal) {
    if (!window.CollectionFeed) return [];
    const products = await window.CollectionFeed.load(key, { signal });
    return this.matchLocal(products, query);
  }

  matchLocal(products, query) {
    const feed = window.CollectionFeed;
    const tokens = feed.tokens(query);
    // Title matches lead: "oil f" should show "Oil Filter" before an engine
    // oil whose only f is the "Four Stroke" in its category. A product
    // matched through its part number, brand or category still shows, after.
    const titled = (p) => tokens.every((t) => feed.tokens(p.title).some((w) => w.startsWith(t)));
    const hits = products.filter((p) => feed.matches(p, tokens));
    const inStock = (p) => (p.variants || []).some((v) => v.available);
    const ordered = hits.filter(titled).concat(hits.filter((p) => !titled(p)));
    // Out of stock after everything in stock — the one place they still show.
    return ordered.filter(inStock).concat(ordered.filter((p) => !inStock(p)))
      .slice(0, this.limit)
      .map((p) => ({
        handle: p.handle,
        title: p.title,
        url: `/products/${p.handle}`,
        featured_image: { url: p.image ?? '' },
        price: p.variants?.[0]?.price ?? '',
      }));
  }

  render(query, groups) {
    const populated = groups.filter((g) => g.products.length || g.pending);
    if (!populated.length) {
      return this.renderMessage(`No results for “${query}”.`);
    }

    const frag = document.createDocumentFragment();
    this.items = [];

    for (const group of populated) {
      const heading = document.createElement('p');
      // The fits group says so once, in its band — not on every row
      // (Lucas, 2026-10-12).
      heading.className = `header__search-group-title${group.fits ? ' header__search-group-title--fits' : ''}`;
      heading.textContent = group.fits ? `✓ ${group.title}` : group.title;
      if (group.note) {
        const note = document.createElement('span');
        note.className = 'header__search-group-note';
        note.textContent = group.note;
        heading.appendChild(note);
      }
      frag.appendChild(heading);

      if (group.pending) {
        const wait = document.createElement('p');
        wait.className = 'header__search-message header__search-message--pending';
        wait.textContent = 'Searching the rest of the store…';
        frag.appendChild(wait);
        continue;
      }

      for (const product of group.products) {
        frag.appendChild(group.machines ? this.machineRow(product) : this.row(product, group.fits));
      }
    }

    this.results.replaceChildren(frag);
    this.open();
  }

  /** A machine (a vehicle collection): its name, and whether it is in the garage. */
  machineRow(collection) {
    const link = document.createElement('a');
    link.className = 'header__search-result header__search-result--machine';
    link.href = `/collections/${collection.handle}`;
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');
    link.id = `search-result-${this.items.length}`;

    const text = document.createElement('span');
    text.className = 'header__search-result-text';
    const title = document.createElement('span');
    title.className = 'header__search-result-title';
    title.textContent = collection.title;
    text.appendChild(title);

    const saved = (window.Garage?.vehicles ?? []).some((v) => v.key === collection.handle);
    const note = document.createElement('span');
    note.className = 'header__search-result-note';
    note.textContent = saved ? 'In your garage' : 'Parts for this machine';
    text.appendChild(note);

    link.appendChild(text);
    this.items.push(link);
    return link;
  }

  row(product, fits = false) {
    const link = document.createElement('a');
    link.className = `header__search-result${fits ? ' header__search-result--fits' : ''}`;
    link.href = product.url;
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');
    link.id = `search-result-${this.items.length}`;

    const image = product.featured_image?.url || product.image;
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.loading = 'lazy';
      link.appendChild(img);
    }

    const text = document.createElement('span');
    text.className = 'header__search-result-text';

    const title = document.createElement('span');
    title.className = 'header__search-result-title';
    title.textContent = product.title; // textContent: titles are merchant data
    text.appendChild(title);

    if (product.price !== undefined && product.price !== '') {
      const price = document.createElement('span');
      price.className = 'header__search-result-price';
      price.textContent = String(product.price).startsWith('$')
        ? product.price
        : `$${product.price}`;
      text.appendChild(price);
    }

    link.appendChild(text);
    this.items.push(link);
    return link;
  }

  renderMessage(message) {
    const p = document.createElement('p');
    p.className = 'header__search-message';
    p.textContent = message;
    this.results.replaceChildren(p);
    this.items = [];
    this.open();
  }

  onKeydown(event) {
    if (event.key === 'Escape') return this.close();
    if (!this.items.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + step + this.items.length) % this.items.length;
      this.highlight();
    } else if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      this.items[this.activeIndex].click();
    }
  }

  highlight() {
    this.items.forEach((item, i) => {
      const selected = i === this.activeIndex;
      item.setAttribute('aria-selected', String(selected));
      if (selected) {
        // Optional-call: keeps a missing scrollIntoView from aborting the loop
        // before aria-activedescendant is set, which is what screen readers use.
        item.scrollIntoView?.({ block: 'nearest' });
        this.input.setAttribute('aria-activedescendant', item.id);
      }
    });
  }

  open() {
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.activeIndex = -1;
  }

  close() {
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
  }

}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
